import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { Feature, accessControl } from "../services/accessControl";
import { insightBridge } from "../services/insightBridge";
import { trackEvent, WingmanAnalytics } from "../services/analyticsService";
import { featureFlags, WingmanFeatureFlags } from "../services/featureFlagService";
import { normalizeCoordinate } from "../spatial/coordinateValidator";

export interface RecommendedPlace {
  name: string;
  rating: number;
  distance: number;
  category: string;
  provider: string;
  score: number;
  confidence: number;
}

export interface AreaAnalysis {
  summary: string;
  areaType: string;
  activityLevel: string;
  quality: string;
  safety: string;
  limitedData?: boolean;
}

export interface VideoInfo {
  title: string;
  url: string;
  thumbnail: string;
  confidence: number;
  coordinates?: [number, number];
  isPremium?: boolean;
  isLive?: boolean;
  category?: string;
  videoId?: string;
  summary?: string;
}

export interface VaultItem {
    id: string;
    videoId: string;
    url: string;
    title: string;
    thumbnail: string;
    location: [number, number]; // [lng, lat] array contract
    category: string;
    savedAt: number;
    summary?: string;
}

export interface Insight {
  action: string;
  message: string;
  priority: number;
  videos?: VideoInfo[];
  /** Structured transcript insight */
  vibe?: string;
  tags?: string[];
  insightSummary?: string;
  insightSummaryShort?: string;
  monetization?: {
    gate: boolean;
    cta: string | null;
    visibility: string;
  };
}

export interface UserProfile {
  preferences: Record<string, number>; // Tag -> Weight (0.0 to 1.0+)
  lastInteracted: number;
  totalWatchTime: number;
}

export interface UpgradePromptState {
    isOpen: boolean;
    feature: Feature | null;
    message: string;
}

const LAUNCH_MEDIA_PACK: VideoInfo[] = [
  {
    title: "Miami Beach Live - Ocean Drive Drone Cam",
    url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    thumbnail: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&q=80&w=800",
    confidence: 0.98,
    coordinates: [-80.1300, 25.7760],
    category: "trending",
    isLive: true,
    tags: ["beach", "ocean", "live"]
  },
  {
    title: "Penthouse at the Faena - Exclusive Walkthrough",
    url: "https://vimeo.com/76979871",
    thumbnail: "https://images.unsplash.com/photo-1582719508461-905c673771fd?auto=format&fit=crop&q=80&w=800",
    confidence: 0.95,
    coordinates: [-80.1215, 25.8078],
    category: "luxury",
    isLive: false,
    tags: ["luxury", "penthouse", "elite"]
  },
  {
    title: "LIV Nightclub VIP Experience",
    url: "https://www.youtube.com/watch?v=kYtGl1dX5qI",
    thumbnail: "https://images.unsplash.com/photo-1566737236500-c8ac43014a67?auto=format&fit=crop&q=80&w=800",
    confidence: 0.92,
    coordinates: [-80.1221, 25.8178],
    category: "nightlife",
    isLive: false,
    tags: ["nightlife", "party", "club"]
  },
  {
    title: "Wynwood Walls Aerial Tour",
    url: "https://vimeo.com/264223190",
    thumbnail: "https://images.unsplash.com/photo-1574301382404-54c7d0d62557?auto=format&fit=crop&q=80&w=800",
    confidence: 0.90,
    coordinates: [-80.1996, 25.8015],
    category: "nature",
    isLive: false,
    tags: ["adventure", "drone", "culture"]
  }
];

const LAUNCH_VAULT_ITEMS: VaultItem[] = LAUNCH_MEDIA_PACK.map(video => ({
    id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(),
    videoId: video.videoId || video.url.split('v=')[1]?.split('&')[0] || video.url.split('vimeo.com/')[1] || 'unknown',
    url: video.url,
    title: video.title,
    thumbnail: video.thumbnail,
    location: video.coordinates ? normalizeCoordinate(video.coordinates) || [0, 20] : [0, 20],
    category: video.category || 'Travel',
    savedAt: Date.now(),
    summary: video.summary || "Curated Launch Intelligence"
}));

interface WingmanState {
  recommendations: RecommendedPlace[];
  areaAnalysis: AreaAnalysis | null;
  activeInsight: Insight | null;
  userTier: 'free' | 'starter' | 'pro' | 'elite' | 'enterprise' | 'developer';
  activeVector: 'VIBE_CHECK' | 'TRANSCRIPT_QUERY' | 'DEEP_RESEARCH' | null;
  targetLocation: [number, number] | null;
  isChatOpen: boolean;
  chatHistory: { role: 'user' | 'assistant'; content: string }[];
  confidence: number;
  locationConfidence: number;
  limitedData: boolean;
  isVibeModeOpen: boolean;
  activeFeedIndex: number;
  userProfile: UserProfile;
  savedVideos: VaultItem[];
  isSavedGalleryOpen: boolean;
  upgradePrompt: UpgradePromptState;
  aiSuggestions: RecommendedPlace[];
  isSearchLocked: boolean;
  activeLocationName: string | null;
  locationQuery: string | null;
  featureFlags: WingmanFeatureFlags;
  
  setRecommendations: (data: RecommendedPlace[]) => void;
  setAreaAnalysis: (data: AreaAnalysis) => void;
  setInsight: (data: Insight | null) => void;
  clearInsight: () => void;
  setTargetLocation: (coords: [number, number] | null) => void;
  setActiveVector: (vector: 'VIBE_CHECK' | 'TRANSCRIPT_QUERY' | 'DEEP_RESEARCH' | null) => void;
  setActiveLocationName: (name: string | null) => void;
  setLocationQuery: (query: string | null) => void;
  setChatOpen: (isOpen: boolean) => void;
  addChatMessage: (msg: { role: 'user' | 'assistant'; content: string }) => void;
  clearChat: () => void;
  setUserTier: (tier: 'free' | 'starter' | 'pro' | 'elite' | 'enterprise' | 'developer') => void;
  setConfidence: (intentConf: number, locConf: number) => void;
  setVibeModeOpen: (isOpen: boolean) => void;
  setActiveFeedIndex: (index: number) => void;
  trackEngagement: (tags: string[], weight: number) => void;
  saveVideo: (video: VideoInfo) => void;
  unsaveVideo: (id: string) => void;
  setSavedGalleryOpen: (isOpen: boolean) => void;
  triggerUpgradePrompt: (feature: Feature) => void;
  closeUpgradePrompt: () => void;
  setAiSuggestions: (data: RecommendedPlace[]) => void;
  applyAiSuggestions: () => void;
  setSearchLocked: (locked: boolean) => void;
  clearSpatialData: () => void;
  selectedVoiceModel: 'azure';
  setSelectedVoiceModel: (model: 'azure') => void;
  selectedVoiceName: string;
  setSelectedVoiceName: (voiceName: string) => void;
  activeBriefing: any | null;
  setActiveBriefing: (briefing: any | null) => void;
  activeSpatialContext: ActiveSpatialContext | null;
  setActiveSpatialContext: (ctx: ActiveSpatialContext | null) => void;
  activeReport: any | null;
  setActiveReport: (report: any | null) => void;
  pendingReportBrief: any | null;
  setPendingReportBrief: (brief: any | null) => void;
  reportAllowance: { allowed: boolean; remaining: number; total: number };
  setReportAllowance: (allowance: Partial<{ allowed: boolean; remaining: number; total: number }>) => void;
  reportStatus: 'idle' | 'clarifying' | 'brief_pending' | 'generating' | 'ready' | 'failed' | 'cancelled' | 'allowance_exhausted' | null;
  setReportStatus: (status: 'idle' | 'clarifying' | 'brief_pending' | 'generating' | 'ready' | 'failed' | 'cancelled' | 'allowance_exhausted' | null) => void;
  reportError: string | null;
  setReportError: (err: string | null) => void;
}

export interface ActiveSpatialContext {
  name: string;
  latitude: number;
  longitude: number;
  region?: string;
  country?: string;
  scope?: 'NODE' | 'LOCAL' | 'METRO' | 'REGIONAL' | 'STATE' | 'NATIONAL' | 'GLOBAL';
  updatedAt: string;
}

// ─── WINGMAN_QUOTA_EXCEEDED Event Listener ──────────────────────────────────
// Registers a global listener that triggers the upgrade prompt when the backend
// returns a QUOTA_EXCEEDED response. This is the ONLY path that should trigger
// the upgrade prompt — UI components must NOT call triggerUpgradePrompt directly.
//
// The backend is the SSOT for quota enforcement. The frontend simply obeys.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('WINGMAN_QUOTA_EXCEEDED', ((event: CustomEvent) => {
        const detail = event.detail;
        console.warn(`[QUOTA] Event received: tier=${detail.tier} used=${detail.used}/${detail.limit}`);
        
        // Trigger the upgrade prompt with the appropriate feature
        const store = useWingmanStore.getState();
        store.triggerUpgradePrompt('SPATIAL_RESEARCH');
        
        // Log telemetry
        trackEvent('quota_exceeded_ui', {
            tier: detail.tier,
            used: detail.used,
            limit: detail.limit,
            timestamp: detail.timestamp || new Date().toISOString()
        });
    }) as EventListener);
}

export const useWingmanStore = create<WingmanState>()(
  persist(
    (set, get) => ({
  recommendations: [],
  areaAnalysis: null,
  activeInsight: null,
  userTier: 'free',
  activeVector: null,
  targetLocation: null,
  isChatOpen: false,
  chatHistory: [],
  confidence: 1,
  locationConfidence: 1,
  limitedData: false,
  isVibeModeOpen: false,
  activeFeedIndex: 0,
  userProfile: {
    preferences: {
      luxury: 0.5,
      nightlife: 0.5,
      nature: 0.5,
      adventure: 0.5
    },
    lastInteracted: Date.now(),
    totalWatchTime: 0
  },
  savedVideos: LAUNCH_VAULT_ITEMS,
  isSavedGalleryOpen: false,
  upgradePrompt: {
    isOpen: false,
    feature: null,
    message: ''
  },
  aiSuggestions: [],
  // Presentation mirrors the server's fail-closed default; it is not authorization.
  isSearchLocked: true,
  activeLocationName: null,
  locationQuery: null,
  featureFlags: featureFlags.getFlags(),
  selectedVoiceModel: 'azure',
  setSelectedVoiceModel: (model) => set({ selectedVoiceModel: model }),
  selectedVoiceName: 'en-US-AvaNeural',
  setSelectedVoiceName: (voiceName) => set({ selectedVoiceName: voiceName }),
  activeBriefing: null,
  setActiveBriefing: (briefing) => set({ activeBriefing: briefing }),
  activeSpatialContext: null,
  setActiveSpatialContext: (ctx) => set({ activeSpatialContext: ctx }),
  activeReport: null,
  setActiveReport: (report) => set({ activeReport: report }),
  pendingReportBrief: null,
  setPendingReportBrief: (brief) => set({ pendingReportBrief: brief }),
  reportAllowance: { allowed: true, remaining: 1, total: 1 },
  setReportAllowance: (allowance) => set((state) => ({
    reportAllowance: { ...state.reportAllowance, ...allowance }
  })),
  reportStatus: 'idle',
  setReportStatus: (status) => set({ reportStatus: status }),
  reportError: null,
  setReportError: (err) => set({ reportError: err }),
  
  setRecommendations: (data) => set((state) => {
    if (state.isSearchLocked) return state;

    // 🛡️ PRIORITY LOCK: Only overwrite if we have actual venues and no premium video active
    const hasPremiumVideo = state.activeInsight?.videos && state.activeInsight.videos.length > 0;
    
    let nextInsight = state.activeInsight;
    if (data.length > 0 && !hasPremiumVideo) {
      nextInsight = insightBridge.bridgeVenuesToInsight(data);
      
      // 🚀 ASYNC UPGRADE: Trigger real-time viral content search
      setTimeout(async () => {
        const viralVideos = await insightBridge.fetchViralVideos(data);
        if (viralVideos.length > 0) {
            set((s) => ({
                activeInsight: {
                    ...s.activeInsight!,
                    videos: viralVideos,
                    message: `Viral content identified for ${data[0].name}`
                }
            }));
        }
      }, 500);
    }
    
    return { 
      recommendations: data,
      activeInsight: nextInsight
    };
  }),
  setAiSuggestions: (data) => set({ aiSuggestions: data }),
  applyAiSuggestions: () => set((state) => ({
    recommendations: state.aiSuggestions,
    activeInsight: insightBridge.bridgeVenuesToInsight(state.aiSuggestions)
  })),
  setSearchLocked: (locked) => set({ isSearchLocked: locked }),
  setAreaAnalysis: (data) => set({ 
    areaAnalysis: data,
    limitedData: data.limitedData || false 
  }),
  setInsight: (data: any) => {
    import('../spatial/utils/telemetry').then(({ telemetry }) => {
      telemetry.log({ subsystem: 'STORE', event: 'INSIGHT_UPDATE', level: 'FORENSIC', payload: data });
    });
    set({ activeInsight: data });
  },
  setUserTier: (tier) => set({ userTier: tier }),
  clearInsight: () => set({ activeInsight: null }),
  setTargetLocation: (coords) => {
    import('../spatial/utils/telemetry').then(({ telemetry }) => {
      telemetry.log({ subsystem: 'STORE', event: 'INTERACTION', level: 'FORENSIC', payload: { action: 'set_target_location', coords } });
    });
    set({ targetLocation: coords });
  },
  setActiveLocationName: (name) => set({ activeLocationName: name }),
  setLocationQuery: (query) => set({ locationQuery: query }),
  setActiveVector: (vector) => {
    console.log(`[KINETIC] VECTOR_SHIFT: ${vector}`);
    set({ activeVector: vector });
  },
  setChatOpen: (isOpen) => set({ isChatOpen: isOpen }),
  addChatMessage: (msg) => set((state) => ({ 
    chatHistory: [...state.chatHistory, msg] 
  })),
  clearChat: () => set({ chatHistory: [] }),
  setConfidence: (intentConf, locConf) => set({
    confidence: intentConf,
    locationConfidence: locConf
  }),
  setVibeModeOpen: (isOpen) => set({ isVibeModeOpen: isOpen }),
  setActiveFeedIndex: (index) => set({ activeFeedIndex: index }),
  trackEngagement: (tags, weight) => set((state) => {
    const newPrefs = { ...state.userProfile.preferences };
    tags.forEach(tag => {
      const current = newPrefs[tag.toLowerCase()] || 0.5;
      newPrefs[tag.toLowerCase()] = Math.min(current + weight, 5.0); // Cap at 5.0
    });
    return {
      userProfile: {
        ...state.userProfile,
        preferences: newPrefs,
        lastInteracted: Date.now()
      }
    };
  }),
  saveVideo: (video) => {
    const { savedVideos } = get();
    const videoId = video.videoId || video.url.split('v=')[1]?.split('&')[0] || 'unknown';
    
    // 🛡️ DEDUPLICATION GUARD: Prevent identical videos from cluttering the vault
    if (savedVideos.some(v => v.url === video.url || (v.videoId === videoId && videoId !== 'unknown'))) {
        console.warn(`[Vault] Video already exists: ${video.title}`);
        return;
    }

    const vaultItem: VaultItem = {
        id: crypto.randomUUID(),
        videoId: video.videoId || video.url.split('v=')[1]?.split('&')[0] || 'unknown',
        url: video.url,
        title: video.title,
        thumbnail: video.thumbnail,
        location: normalizeCoordinate(video.coordinates) || [0, 20],
        category: video.category || 'Travel',
        savedAt: Date.now()
    };

    set({ savedVideos: [...savedVideos, vaultItem] });
    WingmanAnalytics.save(video.title, vaultItem.category);
  },
  unsaveVideo: (id) => set((state) => {
    const video = state.savedVideos.find(v => v.id === id || v.url === id);
    if (video) WingmanAnalytics.unsave(video.title);
    return { 
        savedVideos: state.savedVideos.filter((v) => v.id !== id && v.url !== id) 
    };
  }),
  setSavedGalleryOpen: (isOpen) => {
    if (isOpen) WingmanAnalytics.vaultOpen(get().savedVideos.length);
    set({ isSavedGalleryOpen: isOpen });
  },
  triggerUpgradePrompt: (feature) => set({ 
    upgradePrompt: { 
      isOpen: true, 
      feature, 
      message: accessControl.getUpgradeMessage(feature) 
    } 
  }),
  closeUpgradePrompt: () => set((state) => ({ 
    upgradePrompt: { ...state.upgradePrompt, isOpen: false } 
  })),
  clearSpatialData: () => set({
    recommendations: [],
    areaAnalysis: null,
    activeInsight: null,
    limitedData: false
  }),
  toggleFeature: (feature, enabled) => {
    featureFlags.setFlag(feature, enabled);
    set({ featureFlags: featureFlags.getFlags() });
  }
}), {
  name: 'wingman-brain-storage',
  version: 3, // 🛡️ ATOM W-007: Incrementing version for [lng, lat] contract enforcement
  storage: createJSONStorage(() => localStorage),
  migrate: (persistedState: any, version: number) => {
    console.info(`[Wingman Store] Rehydrating version ${version}...`);
    
    // 🛡️ DATA MIGRATION GUARD: Validate and sanitize persisted vault data
    if (version < 3 || !persistedState.savedVideos) {
        console.warn("[Wingman Store] Legacy or malformed vault detected. Running sanitization.");
        
        const rawVideos = Array.isArray(persistedState.savedVideos) ? persistedState.savedVideos : [];
        const sanitizedVideos = rawVideos.map((v: any) => {
            // Check mandatory fields for VaultItem
            const hasBasic = v && v.id && v.url && v.title;
            
            // Handle legacy {lat, lng} objects or arrays
            const location = normalizeCoordinate(v.location || v.coordinates) || [0, 20];
            
            if (!hasBasic) {
                console.warn(`[Wingman Store] Discarding malformed vault entry: ${v?.title || 'Unknown'}`);
                return null;
            }
            
            return {
                ...v,
                location
            };
        }).filter(Boolean);

        return {
            ...persistedState,
            selectedVoiceModel: 'azure',
            savedVideos: sanitizedVideos
        };
    }

    return {
        ...persistedState,
        selectedVoiceModel: 'azure'
    };
  },
  partialize: (state) => ({ 
    userProfile: state.userProfile,
    savedVideos: state.savedVideos,
    userTier: state.userTier,
    selectedVoiceModel: state.selectedVoiceModel,
    selectedVoiceName: state.selectedVoiceName
  }) // Persist both the brain and the bookmarks
}));
